/**
 * Forderungen-Export (Issue #266).
 *
 * CSV- und XLSX-Export für die Fallübersicht der Forderungsakten. Exportiert
 * wird die aktuell gefilterte + sortierte Menge, nicht die Gesamtmenge. Die
 * CSV ist Excel-DE-freundlich (UTF-8 BOM, Semikolon-Trenner, CRLF). Die XLSX
 * wird komplett aus Strings zusammengesetzt (JSZip ist bereits Dependency,
 * kein zusätzliches Paket nötig) und enthält AutoFilter + Spaltenbreiten.
 */

import JSZip from 'jszip';
import type { Forderungsakte } from '@/hooks/useForderungen';

// ─── Typen ───────────────────────────────────────────────

export type ForderungCellValue = string | number | boolean | null | undefined;

export type ForderungExportRow = Pick<
  Forderungsakte,
  | 'akten_nr'
  | 'unfall_datum'
  | 'unfallgegner_name'
  | 'unfallgegner_kennzeichen'
  | 'versicherung_gegner'
  | 'schaden_nummer'
  | 'fahrzeugklasse_beschaedigt'
  | 'fahrzeugklasse_gemietet'
  | 'klassengleichheit'
  | 'mietbeginn'
  | 'mietende'
  | 'mietdauer_tage'
  | 'tagessatz'
  | 'rechnung_nr'
  | 'rechnung_datum'
  | 'rechnung_betrag'
  | 'gesamtbetrag'
  | 'status'
  | 'mahnstufe'
  | 'risiko_einschaetzung'
  | 'verjährungsfrist'
  | 'anspruchsgrundlage'
  | 'created_at'
>;

export interface ForderungExportColumn {
  key: keyof ForderungExportRow;
  label: string;
  width: number;
  type: 'text' | 'date' | 'number' | 'currency' | 'boolean';
}

const STATUS_LABELS: Record<string, string> = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  abgerechnet: 'Abgerechnet',
  strittig: 'Strittig',
  gerichtlich: 'Gerichtlich',
  erledigt: 'Erledigt',
};

const MAHNSTUFE_LABELS: Record<string, string> = {
  keine: 'Keine',
  '1_mahnung': '1. Mahnung',
  '2_mahnung': '2. Mahnung',
  inkasso: 'Inkasso',
  anwalt: 'Anwalt',
  gerichtlich: 'Gerichtlich',
};

const RISIKO_LABELS: Record<string, string> = {
  stark: 'stark',
  mittel: 'mittel',
  gefaehrdet: 'gefährdet',
};

export const forderungExportColumns: ForderungExportColumn[] = [
  { key: 'akten_nr', label: 'Akten-Nr', width: 18, type: 'text' },
  { key: 'unfall_datum', label: 'Unfall-Datum', width: 13, type: 'date' },
  { key: 'unfallgegner_name', label: 'Unfallgegner', width: 22, type: 'text' },
  { key: 'unfallgegner_kennzeichen', label: 'Kennzeichen', width: 14, type: 'text' },
  { key: 'versicherung_gegner', label: 'Versicherung', width: 20, type: 'text' },
  { key: 'schaden_nummer', label: 'Schaden-Nr', width: 18, type: 'text' },
  { key: 'fahrzeugklasse_beschaedigt', label: 'Klasse beschädigt', width: 22, type: 'text' },
  { key: 'fahrzeugklasse_gemietet', label: 'Klasse gemietet', width: 22, type: 'text' },
  { key: 'klassengleichheit', label: 'Klassengleich', width: 12, type: 'boolean' },
  { key: 'mietbeginn', label: 'Mietbeginn', width: 13, type: 'date' },
  { key: 'mietende', label: 'Mietende', width: 13, type: 'date' },
  { key: 'mietdauer_tage', label: 'Mietdauer (T)', width: 12, type: 'number' },
  { key: 'tagessatz', label: 'Tagessatz', width: 12, type: 'currency' },
  { key: 'rechnung_nr', label: 'Rechnung-Nr', width: 16, type: 'text' },
  { key: 'rechnung_datum', label: 'Rechnung-Datum', width: 14, type: 'date' },
  { key: 'rechnung_betrag', label: 'Rechnung-Betrag', width: 15, type: 'currency' },
  { key: 'gesamtbetrag', label: 'Gesamtbetrag', width: 14, type: 'currency' },
  { key: 'status', label: 'Status', width: 16, type: 'text' },
  { key: 'mahnstufe', label: 'Mahnstufe', width: 14, type: 'text' },
  { key: 'risiko_einschaetzung', label: 'Risiko', width: 11, type: 'text' },
  { key: 'verjährungsfrist', label: 'Verjährung', width: 13, type: 'date' },
  { key: 'anspruchsgrundlage', label: 'Anspruchsgrundlage', width: 22, type: 'text' },
  { key: 'created_at', label: 'Angelegt am', width: 19, type: 'date' },
];

// ─── CSV ─────────────────────────────────────────────────

const BOM = '﻿';
const CRLF = '\r\n';
const DELIM = ';';

export function serializeCsvCell(value: ForderungCellValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    return value.toFixed(2).replace('.', ',');
  }
  const s = String(value);
  if (s === '') return '';
  const needsQuoting =
    s.includes(DELIM) ||
    s.includes('"') ||
    s.includes('\n') ||
    s.includes('\r');
  if (!needsQuoting) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

function formatCsvValue(
  row: ForderungExportRow,
  col: ForderungExportColumn,
): string {
  const raw = row[col.key] as ForderungCellValue;
  if (col.key === 'status' && typeof raw === 'string') {
    return serializeCsvCell(STATUS_LABELS[raw] ?? raw);
  }
  if (col.key === 'mahnstufe' && typeof raw === 'string') {
    return serializeCsvCell(MAHNSTUFE_LABELS[raw] ?? raw);
  }
  if (col.key === 'risiko_einschaetzung' && typeof raw === 'string') {
    return serializeCsvCell(RISIKO_LABELS[raw] ?? raw);
  }
  if (col.type === 'date' && typeof raw === 'string' && raw !== '') {
    // ISO-Datum in DE-Darstellung: 2026-02-14 → 14.02.2026
    // Nur Datums-Teil, keine Zeit. Bei Zeitstempeln (created_at) trotzdem
    // nur das Datum, damit die Spalte lesbar bleibt.
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}.${d.getFullYear()}`;
    }
  }
  return serializeCsvCell(raw);
}

export function toCsv(
  rows: ForderungExportRow[],
  columns: ForderungExportColumn[],
): string {
  const header = columns.map((c) => serializeCsvCell(c.label)).join(DELIM);
  const body = rows
    .map((r) => columns.map((c) => formatCsvValue(r, c)).join(DELIM))
    .join(CRLF);
  const lines = rows.length > 0 ? `${header}${CRLF}${body}${CRLF}` : `${header}${CRLF}`;
  return `${BOM}${lines}`;
}

// ─── XLSX ────────────────────────────────────────────────

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function colLetter(index1: number): string {
  // 1 → A, 26 → Z, 27 → AA
  let n = index1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

interface XlsxCell {
  type: 'string' | 'number';
  value: string | number;
}

function cellFor(
  row: ForderungExportRow,
  col: ForderungExportColumn,
): XlsxCell {
  const raw = row[col.key] as ForderungCellValue;
  if (col.key === 'status' && typeof raw === 'string') {
    return { type: 'string', value: STATUS_LABELS[raw] ?? raw };
  }
  if (col.key === 'mahnstufe' && typeof raw === 'string') {
    return { type: 'string', value: MAHNSTUFE_LABELS[raw] ?? raw };
  }
  if (col.key === 'risiko_einschaetzung' && typeof raw === 'string') {
    return { type: 'string', value: RISIKO_LABELS[raw] ?? raw };
  }
  if (col.type === 'date' && typeof raw === 'string' && raw !== '') {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return { type: 'string', value: `${day}.${month}.${d.getFullYear()}` };
    }
  }
  if (col.type === 'boolean') {
    if (raw === true) return { type: 'string', value: 'ja' };
    if (raw === false) return { type: 'string', value: 'nein' };
    return { type: 'string', value: '' };
  }
  if ((col.type === 'number' || col.type === 'currency') && typeof raw === 'number' && Number.isFinite(raw)) {
    return { type: 'number', value: raw };
  }
  if (raw === null || raw === undefined) return { type: 'string', value: '' };
  return { type: 'string', value: String(raw) };
}

function buildContentTypes(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;
}

function buildRootRels(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
}

function buildWorkbook(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Forderungen" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;
}

function buildWorkbookRels(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

function buildStyles(): string {
  // Minimalstruktur: zwei Styles — default (0) und bold header (1).
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><name val="Calibri"/></font>
  </fonts>
  <fills count="1"><fill><patternFill patternType="none"/></fill></fills>
  <borders count="1"><border/></borders>
  <cellStyleXfs count="1"><xf/></cellStyleXfs>
  <cellXfs count="2">
    <xf fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
  </cellXfs>
</styleSheet>`;
}

function buildSharedStrings(strings: string[]): string {
  const items = strings
    .map((s) => `<si><t xml:space="preserve">${xmlEscape(s)}</t></si>`)
    .join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${strings.length}" uniqueCount="${strings.length}">${items}</sst>`;
}

function buildSheet(
  rows: ForderungExportRow[],
  columns: ForderungExportColumn[],
  sharedStringIndex: (s: string) => number,
): string {
  const lastCol = colLetter(columns.length);
  const lastRow = rows.length + 1;

  const cols = columns
    .map(
      (c, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${c.width}" customWidth="1"/>`,
    )
    .join('');

  const headerCells = columns
    .map((c, i) => {
      const ref = `${colLetter(i + 1)}1`;
      const si = sharedStringIndex(c.label);
      return `<c r="${ref}" t="s" s="1"><v>${si}</v></c>`;
    })
    .join('');
  const header = `<row r="1">${headerCells}</row>`;

  const body = rows
    .map((row, rowIdx) => {
      const r = rowIdx + 2;
      const cells = columns
        .map((col, colIdx) => {
          const ref = `${colLetter(colIdx + 1)}${r}`;
          const cell = cellFor(row, col);
          if (cell.type === 'number') {
            return `<c r="${ref}"><v>${cell.value}</v></c>`;
          }
          const s = String(cell.value);
          if (s === '') {
            return `<c r="${ref}"/>`;
          }
          const si = sharedStringIndex(s);
          return `<c r="${ref}" t="s"><v>${si}</v></c>`;
        })
        .join('');
      return `<row r="${r}">${cells}</row>`;
    })
    .join('');

  const dim = `A1:${lastCol}${lastRow}`;
  const autoFilter = `<autoFilter ref="A1:${lastCol}${lastRow}"/>`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="${dim}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  <cols>${cols}</cols>
  <sheetData>${header}${body}</sheetData>
  ${autoFilter}
</worksheet>`;
}

export async function toXlsx(
  rows: ForderungExportRow[],
  columns: ForderungExportColumn[],
): Promise<Uint8Array> {
  const strings: string[] = [];
  const stringIndex = new Map<string, number>();
  const sharedStringIndex = (s: string): number => {
    const existing = stringIndex.get(s);
    if (existing !== undefined) return existing;
    const idx = strings.length;
    strings.push(s);
    stringIndex.set(s, idx);
    return idx;
  };

  const sheetXml = buildSheet(rows, columns, sharedStringIndex);
  const sharedXml = buildSharedStrings(strings);

  const zip = new JSZip();
  zip.file('[Content_Types].xml', buildContentTypes());
  zip.file('_rels/.rels', buildRootRels());
  zip.file('xl/workbook.xml', buildWorkbook());
  zip.file('xl/_rels/workbook.xml.rels', buildWorkbookRels());
  zip.file('xl/styles.xml', buildStyles());
  zip.file('xl/sharedStrings.xml', sharedXml);
  zip.file('xl/worksheets/sheet1.xml', sheetXml);

  return zip.generateAsync({ type: 'uint8array' });
}

// ─── Filename ────────────────────────────────────────────

export function buildExportFilename(
  prefix: string,
  ext: 'csv' | 'xlsx',
  now: Date = new Date(),
  timeZone: string = 'Europe/Berlin',
): string {
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const yyyy = get('year');
  const mm = get('month');
  const dd = get('day');
  const hh = get('hour') === '24' ? '00' : get('hour');
  const mi = get('minute');
  return `${prefix}_${yyyy}-${mm}-${dd}_${hh}-${mi}.${ext}`;
}

// ─── Browser-Download-Hilfen ─────────────────────────────

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadCsv(
  rows: ForderungExportRow[],
  columns: ForderungExportColumn[] = forderungExportColumns,
): void {
  const csv = toCsv(rows, columns);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, buildExportFilename('forderungen', 'csv'));
}

export async function downloadXlsx(
  rows: ForderungExportRow[],
  columns: ForderungExportColumn[] = forderungExportColumns,
): Promise<void> {
  const buf = await toXlsx(rows, columns);
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, buildExportFilename('forderungen', 'xlsx'));
}
